// characters: GLB персонажей в рантайме — загрузка по требованию, масштаб и
// посадка на пол (как у солдата: bbox по idle-позе, не по bind/T-позе).
// Солдата грузит main.ts своим путём (realBody-эксперимент и т.п.) и
// регистрирует здесь готовым.

import type { Renderer } from "../engine/renderer";
import { canSkinInnerToOuterPose, type LoadedMesh } from "../engine/meshLoader";
import { characterById, isValidCharacterId } from "./registry";

export interface CharacterVisual {
  charId: number;
  mesh: LoadedMesh;
  /** Внутренний меш урона; null — только outer (урон сферами, как раньше). */
  inner: LoadedMesh | null;
  scale: number;
  /** Ступни над полом арены (FLOOR_TOP_Z учтён). */
  floorOffset: number;
}

/** Верх пола арены — как в main.ts (FLOOR_TOP_Z). */
const FLOOR_TOP_Z = 0.0625;

const visuals = new Map<number, CharacterVisual>();
const pending = new Map<number, Promise<CharacterVisual | null>>();
const failed = new Set<number>();

/** Посчитать масштаб/оффсет по реальной позе скининга (idle@t=0). */
export function measureCharacter(
  renderer: Renderer, mesh: LoadedMesh, desiredHeight: number,
): { scale: number; floorOffset: number } {
  const probe = renderer.createMeshInstance({
    mesh, x: 0, y: 0, z: 0, yaw: 0, scale: 1, initialAnim: "idle",
  });
  const bb = renderer.computeSkinnedBboxAtCurrentPose(probe);
  const h = bb.max[1] - bb.min[1];
  const scale = h > 0 ? desiredHeight / h : 1;
  return { scale, floorOffset: -bb.min[1] * scale + FLOOR_TOP_Z };
}

/** Солдат (id 0) загружен main.ts — регистрируем готовым. */
export function registerCharacterVisual(v: CharacterVisual): void {
  visuals.set(v.charId, v);
}

export function getCharacterVisual(charId: number): CharacterVisual | undefined {
  return visuals.get(charId);
}

/**
 * Загрузить персонажа (один раз). null — не удалось (тогда рисуем солдата).
 * load — общий кэш GLB из main.ts (loadMeshCached).
 */
export function ensureCharacterVisual(
  renderer: Renderer,
  load: (url: string) => Promise<LoadedMesh>,
  charId: number,
): Promise<CharacterVisual | null> {
  if (!isValidCharacterId(charId)) charId = 0;
  const ready = visuals.get(charId);
  if (ready) return Promise.resolve(ready);
  if (failed.has(charId)) return Promise.resolve(null);
  let p = pending.get(charId);
  if (p) return p;
  const def = characterById(charId);
  p = (async (): Promise<CharacterVisual | null> => {
    try {
      const [mesh, innerRaw] = await Promise.all([
        load(def.model),
        load(def.inner).catch(() => null),
      ]);
      let inner: LoadedMesh | null = innerRaw;
      if (inner && !canSkinInnerToOuterPose(mesh, inner)) {
        console.warn(`[characters] ${def.inner}: кости не совпадают с ${def.model} — без внутреннего меша`);
        inner = null;
      }
      const { scale, floorOffset } = measureCharacter(renderer, mesh, def.desiredHeight);
      const v: CharacterVisual = { charId, mesh, inner, scale, floorOffset };
      visuals.set(charId, v);
      console.log(`[characters] ${def.key} загружен (scale=${scale.toFixed(3)}, floor=${floorOffset.toFixed(3)}, inner=${inner ? "да" : "нет"})`);
      return v;
    } catch (e) {
      console.warn(`[characters] ${def.model} не загрузился:`, e);
      failed.add(charId);
      return null;
    } finally {
      pending.delete(charId);
    }
  })();
  pending.set(charId, p);
  return p;
}
